import { FeatureFlag } from '@prisma/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateFeatureFlagArgs,
  EditFeatureFlagArgs,
  FindAllFeatureFlagsOmitAuditResult,
  FindFeatureFlagsArgs,
} from '@app/infra-interfaces';

import { useAxiosApi } from '../axios';

const FEATURE_FLAG_ALL_QUERY_KEY = 'FEATURE_FLAG_ALL_QUERY_KEY';

export const featureFlagKeys = {
  all: [FEATURE_FLAG_ALL_QUERY_KEY] as const,
  lists: () => [...featureFlagKeys.all, 'list'] as const,
  list: (filters?: object) => [...featureFlagKeys.lists(), filters] as const,
};

export const useFindAllFeatureFlags = () => {
  const axios = useAxiosApi();

  return useQuery<FindAllFeatureFlagsOmitAuditResult[]>({
    queryKey: featureFlagKeys.list({ select: { isEnabled: true, key: true } }),
    queryFn: async () => {
      const { data } = await axios.get('/feature-flags');
      return data;
    },
    meta: { errorMessage: 'Failed to get feature flags' },
    staleTime: Infinity,
  });
};

export const useFindFeatureFlags = (args?: FindFeatureFlagsArgs) => {
  const axios = useAxiosApi();

  return useQuery<FeatureFlag[]>({
    queryKey: featureFlagKeys.list(featureFlagKeys),
    queryFn: async () => {
      const { data } = await axios.post('/feature-flags/find', args);
      return data;
    },
  });
};

export const useCreateFeatureFlag = () => {
  const axios = useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: CreateFeatureFlagArgs) => {
      return axios.post<FeatureFlag>(`/feature-flags`, args);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.lists() });
    },
  });
};

export const useUpdateFeatureFlag = () => {
  const axios = useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, ...dto }: EditFeatureFlagArgs & { key: string }) => {
      return axios.patch<FeatureFlag>(`/feature-flags/${key}`, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.lists() });
    },
  });
};

export const useDeleteFeatureFlag = () => {
  const axios = useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => {
      return axios.delete<FeatureFlag>(`/feature-flags/${key}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagKeys.lists() });
    },
  });
};
